import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Box, Tooltip, IconButton } from '@mui/material';

/** Material UI Icons. */
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Browser Capture tool.   */
import html2canvas from 'html2canvas';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { periodList, shipmentType, containerSizeList, transportList } from 'shared/config/constantsData';
import { AutocompleteFieldTradePartner } from 'shared/ui/AutoCompleteForm';
import { SelectField_single } from 'shared/ui/SelectForm';
import { CircularLoading } from 'shared/ui/CircularLoading';

import validateFields from 'shared/lib/validateFields';
import { operationNow } from 'shared/lib/dateFormatter';

/** Initial input validator. */
const initValidator = {
    client: false,
    period: false,
    year: false,
};

/** Initial filter condition. */
const initFilter = {
    client: {},
    shipper: {},
    consignee: {},
    period: '',
    year: '',
    type: '',
    transport: '',
};

/** Year options starting from 2025 */
const startYear = 2025;
// 연말에 한국에서 열어도 아직 오지 않은 해가 선택지에 먼저 뜨지 않도록 근무지 달력을 따른다.
const currentYear = operationNow().year();
const yearsArray = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);

export default function ChartFilter({ mode, chartRef, tradePartnerList, stateSetter }) {
    /** Retrieves user information and access token silently from Auth0 for authentication and API requests. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Dispatch function to update the Redux store. */
    const dispatch = useDispatch();

    /** Stores all filter criteria used for searching chart data. */
    const [filterCondition, setFilterCondition] = useState(initFilter);
    /** State to manage a validation when call to search. */
    const [validator, setValidator] = useState(initValidator);
    /** State to track whether the user has performed a search. */
    const [hasSearched, setHasSearched] = useState(false);
    /** State to manage loading state for circular loading when search and download */
    const [searchLoadiong, setSearchLoading] = useState(false);
    /** State for controls whether the "Year" dropdown should be disabled based on the selected Period. */
    const [isYearDisabled, setIsYearDisabled] = useState(false);
    /* State for controls whether if "Type" dropdown should be disabled when "FTL/LTL" is selected. */
    const [isTransportDisabled, setIsTransportDisabled] = useState(false);

    /** Updates the filter condition state and validator state. */
    const handleFilterChange = (key, value) => {
        setFilterCondition((prevState) => {
            const newState = {
                ...prevState,
                [key]: value,
            };

            // Yearly → disable year dropdown
            if (key === 'period') {
                setIsYearDisabled(value === 'Yearly');
                if (value === 'Yearly') {
                    newState.year = '';
                }
            }

            // FTL/LTL → disable transport dropdown
            if (key === 'type') {
                setIsTransportDisabled(value === 'FTL/LTL');
                if (value === 'FTL/LTL') {
                    newState.transport = '';
                }
            }

            return newState;
        });
    };

    /** Handler to submit filter condition to the server and receive chart data in response.  */
    const handleSearch = () => {
        // Define validation rules.
        const validationRules = {
            client: () => !!filterCondition.client?.id,
            period: () => !!filterCondition.period || !!filterCondition.year,
            year: () => {
                if (filterCondition.period === 'Yearly') return true;
                return !!filterCondition.year;
            },
        };

        const isValid = validateFields(validationRules, setValidator);

        if (!isValid) {
            return dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please complete the required input fields before proceeding.',
                    severity: 'warning',
                })
            );
        }

        // Call Post API to fetch chart filtered data.
        callAPI({
            method: 'post',
            module: 'analytics',
            component: mode,
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(filterCondition),
            onInit: () => setSearchLoading(true),
            onSuccess: (resp) => {
                stateSetter({ data: resp.data.partner_stat, label: resp.data.label });
                setHasSearched(true);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong, please try again.',
                        severity: 'error',
                    })
                ),
            onFinal: () => setSearchLoading(false),
        });
    };

    /** Function to download an image of Line/Bar chart. */
    const handleDownload = () => {
        if (!hasSearched) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please search before downloading the image.',
                    severity: 'warning',
                })
            );
            return;
        }

        setSearchLoading(true);
        html2canvas(chartRef.current)
            .then((canvas) => {
                const link = document.createElement('a');
                link.href = canvas.toDataURL('image/png'); // set up file format
                link.download = 'chart.png'; // set up file name
                link.click();
                setSearchLoading(false);
            })
            .catch((error) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Failed to download the chart, please try again.',
                        severity: 'error',
                    })
                );
            });
    };

    const handleReset = () => {
        setFilterCondition(initFilter);
        setValidator(initValidator);
        setIsYearDisabled(false);
        setIsTransportDisabled(false);
    };

    /** Render Client, Shipper, and Consignee which are an autocomplete field when page render. */
    const renderTradePartnerField = (key, label, labelWidth = '60px') => {
        const isClient = key === 'client';
        return (
            <AutocompleteFieldTradePartner
                key={key}
                label={label}
                name={key}
                value={filterCondition[key]}
                onChange={(_, newValue) => {
                    handleFilterChange(key, newValue);
                    if (isClient) setValidator((prev) => ({ ...prev, client: false }));
                }}
                options={Array.isArray(tradePartnerList?.[key]) ? tradePartnerList[key] : []}
                sx={{ gap: 0, mr: 1 }}
                labelStyle={{ width: labelWidth }}
                AutocompleteStyle={{ width: '200px' }}
                disable
                {...(isClient ? { error: validator.client } : {})}
            />
        );
    };

    return (
        <>
            <CircularLoading loading={searchLoadiong} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', rowGap: 0.75, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.75 }}>
                    {renderTradePartnerField('client', 'Client', '50px')}
                    {renderTradePartnerField('shipper', 'Shipper')}
                    {renderTradePartnerField('consignee', 'Consignee', '75px')}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: '10px', rowGap: 0.75 }}>
                <SelectField_single
                    label={'Period'}
                    options={periodList}
                    value={filterCondition.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                    sx={{ gap: 0 }}
                    labelStyle={{ width: '50px' }}
                    selectStyle={{ width: '100px' }}
                    error={validator.period}
                />
                <SelectField_single
                    label={'Year'}
                    options={yearsArray}
                    value={filterCondition.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    sx={{ gap: 0 }}
                    labelStyle={{ width: '40px' }}
                    selectStyle={{ width: '100px' }}
                    disabled={isYearDisabled}
                    error={validator.year}
                />
                <SelectField_single
                    label={'Type'}
                    options={mode === 'shipment' ? shipmentType : containerSizeList}
                    value={filterCondition.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    sx={{ gap: 0 }}
                    labelStyle={{ width: '40px' }}
                    selectStyle={{ width: '100px' }}
                />
                <SelectField_single
                    label={'Transport'}
                    options={transportList}
                    value={filterCondition.transport}
                    onChange={(e) => handleFilterChange('transport', e.target.value)}
                    sx={{ gap: 3 }}
                    labelStyle={{ width: '45px' }}
                    selectStyle={{ width: '100px' }}
                    disabled={isTransportDisabled}
                />
                <Tooltip title="Search">
                    <IconButton sx={{ color: 'white', p: 0.2, bgcolor: '#003a52' }} onClick={handleSearch}>
                        <SearchIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                    <IconButton sx={{ color: 'white', p: 0.2, bgcolor: '#003a52' }} onClick={handleDownload}>
                        <FileDownloadIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Reset">
                    <IconButton sx={{ color: '#003a52', p: 0.2, bgcolor: 'white' }} onClick={handleReset}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </>
    );
}
